import * as _ from 'lodash';
import { connect, Dispatch } from 'react-redux';
import { AppActionCreators, IApplicationState, IAppState, MatchActionCreators, IMatchDeadlineListResponse } from '@nkr/store/index';
import { MatchHistoryType } from '@nkr/enums/match-history-type';
import { IBaseResponse, IMatchDeadline } from '@nkr/models/index';
import { Component, FormEvent } from 'react';
import Tabs from './tabs';
import { TableWrapper } from './styles';
import DeadlineRow from './row';
import ConfirmNoResponse from './confirm-no-response';
import { toast } from 'react-toastify';
import { AxiosError } from 'axios';
import { FormControl } from 'react-bootstrap';

interface IProps {
    type: MatchHistoryType
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<any>,
    hideLoading: () => void,
    getByType: (type: MatchHistoryType) => Promise<IMatchDeadlineListResponse>,
    setNoResponse: (id: number) => Promise<IBaseResponse>,
    updateDeadlineById: (id: number, deadline: string) => Promise<IBaseResponse>
}

type TProps =
    IProps
    & IStateProps
    & IDispatchProps;

interface IState {
    list: IMatchDeadline[],
    centerId: number,
    confirm: {
        show: boolean,
        data: IMatchDeadline
    }
}

class MatchDeadlines extends Component<TProps, IState> {
    readonly state: IState = {
        list: [],
        centerId: 0,
        confirm: {
            show: false,
            data: null
        }
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Getting matches, one moment please...').then(() => {
            this.props.getByType(this.props.type).then(response => {
                if (response.data.success) {
                    this.setState({
                        list: response.data.results
                    });
                }

                this.props.hideLoading();
            })
        });
    }

    handleUpdateDeadline = (id: number, deadline: string) => {
        this.props.showLoading('Updating deadline, one moment please...').then(() => {
            this.props.updateDeadlineById(id, deadline).then(response => {
                if (response.data.success) {
                    this.getData();
                    toast.success('Deadline updated successfully');
                }
            }).catch((error: AxiosError) => {
                toast.error('Unable to update deadline: ' + error.response.data, { autoClose: 5000 });
            }).finally(() => {
                this.props.hideLoading();
            });
        });
    }

    handleConfirmNoResponse = (id: number) => {
        let data = this.state.list.find(x => x.id == id);

        this.setState({
            confirm: {
                show: true,
                data
            }
        });
    }

    handleConfirmNoResponseYes = () => {
        let id = this.state.confirm.data.id;

        this.setState({
            confirm: {
                show: false,
                data: null
            }
        }, () => {
            this.props.showLoading('Marking no-response, one moment please...').then(() => {
                this.props.setNoResponse(id).then(response => {
                    if (response.data.success) {
                        this.getData();

                        toast.success('Updated successfully');
                    }
                }).catch(error => {
                    this.props.hideLoading();
                    toast.error('Unable to set response');
                });
            });
        });
    }

    handleConfirmNoResponseNo = () => {
        this.setState({
            confirm: {
                show: false,
                data: null
            }
        });
    }

    handleCenterChange = (e: FormEvent<FormControl & HTMLSelectElement>) => {
        this.setState({
            centerId: parseInt(e.currentTarget.value)
        });
    }

    render = () => {
        const centerList = _.sortBy(_.uniqBy(this.state.list.map(x => {
            return {
                id: x.recipientCenterId,
                alias: x.recipientCenter
            }
        }), x => x.id), x => x.alias);

        const filteredList = this.state.centerId > 0 ? this.state.list.filter(x => x.recipientCenterId == this.state.centerId) : this.state.list;

        return (
            <>
                <Tabs activeKey={this.props.type == MatchHistoryType.MatchOffer ? 'MATCH-OFFER' : 'DONOR-CLEARANCE'} />

                <div style={{ padding: 10, borderLeft: '1px solid #ccc', borderRight: '1px solid #ccc', borderBottom: '1px solid #ccc', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
                        <div style={{ marginRight: 5 }}>Recipient Center:</div>
                        <FormControl
                            componentClass='select'
                            style={{ width: 'initial' }}
                            value={this.state.centerId}
                            onChange={this.handleCenterChange}
                        >
                            <option value={0}>- All Recipient Centers -</option>
                            {centerList.map(item =>
                                <option key={'ctr-' + item.id} value={item.id}>{item.alias}</option>
                            )}
                        </FormControl>
                    </div >

                    <TableWrapper condensed striped bordered style={{ marginBottom: 0 }}>
                        <thead>
                            <tr>
                                <th>Chain</th>
                                <th>Cluster</th>
                                <th>Position</th>
                                <th>Donor Center</th>
                                <th>Donor Alias</th>
                                <th>Recipient Center</th>
                                <th>Recipient Alias</th>
                                <th>Recipient Avoids</th>
                                <th style={{ width: 150 }}>Deadline (EST)</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredList.map((item, index) =>
                                <DeadlineRow
                                    key={'row=' + index}
                                    data={item}
                                    onUpdateDeadline={this.handleUpdateDeadline}
                                    onConfirmNoResponse={this.handleConfirmNoResponse} />
                            )}
                        </tbody>
                    </TableWrapper>
                    <div>{filteredList.length} / {this.state.list.length} records...</div>
                </div>

                <ConfirmNoResponse
                    show={this.state.confirm.show}
                    data={this.state.confirm.data}
                    type={this.props.type}
                    onYes={this.handleConfirmNoResponseYes}
                    onNo={this.handleConfirmNoResponseNo}
                />
            </>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return ({
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getByType: (type) => {
            return dispatch(MatchActionCreators.getByType(type));
        },
        setNoResponse: (id) => {
            return dispatch(MatchActionCreators.setNoResponse(id));
        },
        updateDeadlineById: (id, deadline) => {
            return dispatch(MatchActionCreators.updateDeadlineById(id, deadline));
        },
    });
}

export default connect<{}, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MatchDeadlines);